import type { ProBuilderConsent, ProBuilderConsentLine } from '~/hooks/queries/use-pro-builder-consents';
import { proBuilderConsentTimeLeft } from './pro-builder-copy';

/**
 * FRONT-3176: the consent card's derived view, pure so it is testable without
 * a clock or a DOM. The card itself (`pro-builder-consent-card.tsx`) only
 * draws what these return.
 */

/** Under this much time left the countdown turns amber. */
export const CONSENT_LAST_MINUTE_MS = 60_000;

/**
 * The countdown, e.g. "9:42 left", from the card's own expiry. The browser's
 * clock can sit a little off the server's, so the time left is clamped to the
 * card's full wait and never goes below zero. Null when a time does not
 * parse: no pill beats a wrong one.
 */
export function consentTimeLeft(
  consent: Pick<ProBuilderConsent, 'createdAt' | 'expiresAt'>,
  now: number,
): { label: string; lastMinute: boolean } | null {
  const created = Date.parse(consent.createdAt);
  const expires = Date.parse(consent.expiresAt);
  if (!Number.isFinite(created) || !Number.isFinite(expires) || !Number.isFinite(now) || expires < created) return null;
  const left = Math.min(Math.max(0, expires - now), expires - created);
  // Whole seconds rounded down, so the clock turns amber exactly when it first reads 0:59.
  const seconds = Math.floor(left / 1000);
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return { label: proBuilderConsentTimeLeft(clock), lastMinute: left < CONSENT_LAST_MINUTE_MS };
}

/** The card's title: the server's heading ("Changes to Neuseeland:") without its trailing colon. */
export function consentTitle(heading: string): string {
  return heading.replace(/\s*:\s*$/, '');
}

/** The changes go in the list; a notice (who can use what the change gives) is set apart below it. */
export function splitConsentLines(lines: ProBuilderConsentLine[]): {
  changes: ProBuilderConsentLine[];
  notices: ProBuilderConsentLine[];
} {
  return {
    changes: lines.filter((l) => l.kind !== 'notice'),
    notices: lines.filter((l) => l.kind === 'notice'),
  };
}

/** A notice's bold opening and the rest; no bold part unless the text really starts with it. */
export function noticeParts(line: ProBuilderConsentLine): { lead: string | null; rest: string } {
  if (line.lead && line.text.startsWith(line.lead)) {
    return { lead: line.lead, rest: line.text.slice(line.lead.length).trimStart() };
  }
  return { lead: null, rest: line.text };
}
